import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { env } from "@/shared/config/env";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { TemplateGallerySkeleton, CustomButton } from "@/shared/ui";
import { useAppTheme } from "@/shared/hooks/useTheme";
import { useEditorAuthBridge } from "./hooks/useEditorAuthBridge";
import { useStudioStoreId } from "./hooks/useStudioStoreId";
import {
  useStudioLifecycle,
  type StudioLifecycleHandlers,
  type StudioErrorCode,
} from "./hooks/useStudioLifecycle";

/**
 * Nearest scrolling ancestor. The dashboard scrolls `<main class="flex-1
 * overflow-auto">`, NOT the window — so window scroll math and window scroll
 * listeners both miss it.
 */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (
      /(auto|scroll|overlay)/.test(overflowY) &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * How long after the iframe's `load` we wait for `studio:content-ready`
 * before revealing the frame anyway.
 *
 * A backstop, not a path. Every Studio surface the host embeds posts
 * content-ready on all of its terminal states, so this only fires when the
 * embedded build predates that message or is broken — and then it is
 * deliberately long: a slow catalogue fetch must not be cut off by the timer
 * and dumped onto the Studio's own loader, which is the exact handoff this
 * whole mechanism exists to remove. A stale deployment showing a long
 * skeleton is a visible, diagnosable problem; preempting a slow-but-working
 * fetch is a silent one.
 *
 * Still split by surface: the editor is one screen with one fetch, the
 * gallery loads a catalogue.
 */
const STUDIO_PAINT_FALLBACK_MS = { chrome: 6000, viewport: 4000 } as const;

/**
 * How long the loader lingers, transparent, after the Studio reports a paint.
 * Long enough to cover the compositing gap underneath it; short enough that
 * nobody waits on it. Must match the CSS transition on the overlay.
 */
const SKELETON_FADE_MS = 200;

/**
 * Floor for a chrome-filled frame, held even after the Studio reports a
 * height. See the note at the wrapper style: a failure screen reports a tiny
 * document, and tracking it exactly collapsed the frame — and the error
 * overlay inside it — into a stub under the page header.
 */
const CHROME_MIN_HEIGHT = "calc(100vh - 200px)";

/**
 * The languages the Studio's own chrome ships, and the only values its `?lng=`
 * accepts.
 *
 * Narrowed rather than forwarded verbatim because i18next hands back whatever
 * it resolved — which can be a region tag like "en-US" from browser detection,
 * or a value the Studio has no bundle for. Sending one of those would put the
 * Studio on its fallback while claiming a locale was requested, which is worse
 * to debug than sending nothing.
 */
/**
 * How long after the iframe's `load` we wait for the Studio to say ANYTHING
 * before calling it unreachable.
 *
 * It posts `studio:ready` from its mount — measured at ~250ms — so this is not
 * a race, it is a diagnosis. Set well past the paint fallback so a merely slow
 * screen reveals normally and only a genuinely mute frame trips it.
 */
const STUDIO_SILENCE_MS = 12000;

const STUDIO_LANGS = ["en", "ne", "hi"] as const;

function toStudioLng(raw: string | undefined): (typeof STUDIO_LANGS)[number] {
  const base = (raw ?? "").toLowerCase().split("-")[0];
  return (STUDIO_LANGS as readonly string[]).includes(base)
    ? (base as (typeof STUDIO_LANGS)[number])
    : "en";
}

export interface TemplateStudioFrameProps {
  /** Path within the Studio origin, e.g. "/edit", "/preview", "/templates". */
  studioPath: string;
  /** Studio-side resource id, appended as ?templateId=. */
  templateId?: string;
  /** Accessible frame title; also the test handle. */
  title: string;
  /** Optional host reactions to the Studio's session events. */
  lifecycle?: StudioLifecycleHandlers;
  /**
   * How the frame claims its height.
   * - "chrome": in-flow page content inside DashboardLayout. The Studio
   *   reports its document height over studio:resize and the frame tracks it
   *   exactly, so the frame never scrolls internally — the DASHBOARD's
   *   scrollbar is the only one the vendor sees, like any native page.
   * - "viewport": top-level route with no dashboard chrome (editor/preview)
   *   — the frame IS the page, so it takes the full dynamic viewport.
   */
  fill: "chrome" | "viewport";
  /**
   * Which loading state covers the frame until the Studio reports content.
   *
   * Defaults to the one that matches `fill` — the gallery replica for
   * "chrome", the editor's spinner for "viewport". Override it when a chrome
   * frame is NOT the gallery: the onboarding preview sits inside a phone/
   * tablet shell, where a full gallery skeleton reads as the wrong screen
   * rather than as this screen loading.
   */
  loadingVariant?: "gallery" | "spinner";
  /**
   * The store's business-type code, appended as ?businessTypeCode= so the
   * Studio's gallery can filter to it on its FIRST render — the vendor picked
   * one type at onboarding and legacy never showed the others. Passed on the
   * URL rather than over studio:context because a message arriving after the
   * gallery's query would mean loading everything and then reloading.
   */
  businessTypeCode?: string;
  /**
   * The merchant's own category preset, in the backend's uppercase spelling
   * ("ALL", "CLOTHING", …), appended as ?categoryPreset=.
   *
   * Only the onboarding preview sends this. It outranks the template's own
   * preset Studio-side, which is the point: what the merchant said they sell
   * should pick the demo imagery, not what the template shipped with. The
   * Studio validates it and ignores anything it does not recognise, so a
   * stale enum degrades to the template's default rather than to no images.
   */
  categoryPreset?: string;
  /**
   * Run the gallery as a PICKER — appended as `?mode=select`.
   *
   * Only onboarding's theme step sends this. The Studio's gallery then reports
   * the merchant's choice over `studio:template-selected` (see
   * `lifecycle.onTemplateSelected`) instead of adopting the template and
   * opening the editor, because inside the wizard the choice has to go through
   * the onboarding step API — and opening the editor would walk the merchant
   * out of the flow.
   *
   * Meaningless on any path but "/templates", and absent everywhere else, so
   * the store's own themes page is unaffected.
   */
  picker?: boolean;
  /**
   * Picker mode: the host's current selection, posted over studio:context so
   * the gallery can mark that card as selected.
   */
  selectedTemplateVersionId?: string;
  /**
   * Size the frame to an explicit pixel height and let the Studio scroll
   * INSIDE it, instead of growing the frame to the Studio's reported
   * document height.
   *
   * For a fixed-size preview card this is the honest model — the card is a
   * viewport, so the frame should be one. It also drops the dependency on
   * `studio:resize`, which /preview currently reports once, before its
   * content exists, and never again (see
   * docs/template-studio/STUDIO_PREVIEW_HEIGHT_STUCK.md). Chrome-filled
   * surfaces that genuinely grow the page — the gallery — must NOT set
   * this; they still track the reported height.
   */
  viewportHeight?: number;
  "data-cy": string;
}

export function TemplateStudioFrame({
  studioPath,
  templateId,
  title,
  lifecycle,
  fill,
  loadingVariant,
  businessTypeCode,
  categoryPreset,
  picker,
  selectedTemplateVersionId,
  viewportHeight,
  "data-cy": dataCy,
}: TemplateStudioFrameProps) {
  // Defaults to the loader that matches the height model; an explicit variant
  // wins so a chrome-filled frame that is not the gallery can say so.
  const showSpinner = (loadingVariant ?? (fill === "viewport" ? "spinner" : "gallery")) === "spinner";
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const storeId = useStudioStoreId();
  const { getAccessTokenSilently } = useAuth0();

  useEditorAuthBridge(iframeRef, storeId, getAccessTokenSilently);

  // "chrome" fill: the Studio's gallery reports its document height over
  // studio:resize; tracking it exactly is what keeps the frame from ever
  // growing its own scrollbar. null until the first report (fallback below).
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  // While the Studio has a full-screen overlay up (template preview modal),
  // the iframe expands to cover the whole window — a modal confined to the
  // content-height frame can neither cover the dashboard chrome nor give the
  // storefront renderer a full-width viewport.
  const [overlayOpen, setOverlayOpen] = useState(false);

  // While the Studio's page guide runs, its mask dims the frame area only —
  // the host dims everything OUTSIDE the frame with veil strips so the dark
  // overlay is wall-to-wall (sidebar/header included) while the layout stays
  // put and the page keeps scrolling (the guide drives studio:scroll-to).
  const [dimOn, setDimOn] = useState(false);
  // The un-dimmed hole, in viewport coordinates: the frame's rect clipped to
  // its scroll container's rect. Clipping is what keeps the dashboard's own
  // chrome dimmed at every scroll position — the sticky header sits above the
  // scroll container and the fixed sidebar beside it, so both fall OUTSIDE
  // the hole automatically, with no hardcoded chrome geometry.
  const [dimHole, setDimHole] = useState<{
    top: number;
    left: number;
    right: number;
    bottom: number;
  } | null>(null);

  useEffect(() => {
    if (!dimOn) {
      setDimHole(null);
      return;
    }
    let raf = 0;
    const measure = () => {
      raf = 0;
      const frame = iframeRef.current;
      if (!frame) return;
      const fr = frame.getBoundingClientRect();
      const scroller = findScrollParent(frame);
      const bounds = scroller
        ? scroller.getBoundingClientRect()
        : ({
            top: 0,
            left: 0,
            right: window.innerWidth,
            bottom: window.innerHeight,
          } as DOMRect);
      // Sticky/fixed dashboard chrome keeps painting over the frame's rect at
      // every scroll offset, so the hole must start below/right of it —
      // otherwise scrolling down leaves the header bright while the rest
      // dims. The layout marks its own chrome (data-studio-chrome) rather
      // than this feature hardcoding a header height or sidebar width.
      const chromeEdge = (side: "top" | "left"): number => {
        const el = document.querySelector<HTMLElement>(
          `[data-studio-chrome="${side}"]`,
        );
        if (!el) return 0;
        const r = el.getBoundingClientRect();
        // display:none chrome (collapsed sidebar below lg:) measures 0×0.
        if (r.width === 0 && r.height === 0) return 0;
        return side === "top" ? r.bottom : r.right;
      };
      setDimHole({
        top: Math.max(fr.top, bounds.top, chromeEdge("top"), 0),
        left: Math.max(fr.left, bounds.left, chromeEdge("left"), 0),
        right: Math.min(fr.right, bounds.right, window.innerWidth),
        bottom: Math.min(fr.bottom, bounds.bottom, window.innerHeight),
      });
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    // Capture phase on document: scroll events don't bubble, so a window
    // listener never hears the dashboard's inner scroll container.
    document.addEventListener("scroll", schedule, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", schedule);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
    };
  }, [dimOn]);

  // Indirection so the studio:ready handler below — registered before
  // postCurrentContext is defined — can still reach the current one.
  const postCurrentContextRef = useRef<() => void>(() => {});

  // Whether this Studio document's `studio:ready` has been answered. The
  // handshake is once per document: the reply exists only to cover a context
  // posted before the Studio's listener was installed, and after one answer
  // there is no such gap left. Reset whenever a new document loads (below).
  //
  // Answering every `studio:ready` froze the Templates page for any merchant
  // whose dashboard language differed from the one the Studio had saved on
  // its own origin (13 Oct, P1). Our context switched the Studio's language;
  // the switch remounted its providers, which restored the saved language and
  // re-announced ready; we answered with our language again — doubling every
  // round until the tab locked up. Live theme/locale changes don't ride this
  // path: they are re-posted by the effect on postCurrentContext below.
  const readyAnsweredRef = useRef(false);

  // The iframe's `load` event is too early to drop the loader: it means the
  // DOCUMENT finished, not that the Studio has anything on screen. Neither is
  // studio:ready or studio:resize — both fire from the Studio's MOUNT, before
  // its own loading state paints and well before its data lands. Those two
  // were briefly pressed into service as paint proxies here, and that is what
  // produced the reported sequence: our skeleton, then the Studio's, with the
  // occasional blank frame between.
  //
  // studio:content-ready is the one signal that means the wait is over, on
  // every terminal state including the failures. See `showSkeleton` below.
  const [studioPainted, setStudioPainted] = useState(false);

  // Set from studio:error. Held separately from the paint gate because the
  // Studio posts content-ready straight after the error (so hosts that ignore
  // the message still reveal), which means the loader WILL drop — the error
  // state has to outrank the revealed frame rather than race it.
  const [studioError, setStudioError] = useState<{
    code: StudioErrorCode | "HOST_NO_RESPONSE";
    message?: string;
  } | null>(null);

  // Did the Studio ever say anything at all?
  //
  // Its own postMessages carry a target origin, so a Studio built for the wrong
  // dashboard (VITE_DASHBOARD_ORIGIN pointing at dev while it is embedded in
  // staging) has every message refused by the browser — silently, from our
  // side. The iframe loads, paints, and never speaks; the token handshake never
  // even arrives, so the bridge's own deadline cannot fire either, because
  // nothing started it. Indistinguishable from "still loading" unless we look.
  //
  // A separate listener rather than a flag threaded through every lifecycle
  // handler: this only asks "was there traffic", so it should not have to be
  // kept in step with the message vocabulary.
  const [heardFromStudio, setHeardFromStudio] = useState(false);
  useEffect(() => {
    if (heardFromStudio) return;
    const onAny = (event: MessageEvent) => {
      if (event.origin !== env.STUDIO_ORIGIN) return;
      const type = (event.data as { type?: unknown } | null)?.type;
      if (typeof type === "string" && type.startsWith("studio:")) {
        setHeardFromStudio(true);
      }
    };
    window.addEventListener("message", onAny);
    return () => window.removeEventListener("message", onAny);
  }, [heardFromStudio]);

  // Bumped to remount the iframe on retry. A fresh element is the only way to
  // re-run the Studio's boot: same `src` assigned again is a no-op, and the
  // sandbox gives us no handle on the inner document to reload it.
  const [reloadKey, setReloadKey] = useState(0);

  const { postContext } = useStudioLifecycle(iframeRef, {
    ...lifecycle,
    onReady: () => {
      // The Studio just confirmed its own context listener is installed.
      // Re-post: the load-event context may have been sent into the gap
      // before that listener existed and silently dropped, which leaves the
      // Studio on its embedded default (light) under a dark dashboard.
      //
      // Context handshake ONLY. It says React mounted, which is not the same
      // as anything being on screen — see studio:content-ready below.
      //
      // Once per document — see readyAnsweredRef.
      if (!readyAnsweredRef.current) {
        readyAnsweredRef.current = true;
        postCurrentContextRef.current();
      }
      lifecycle?.onReady?.();
    },
    onResize: (height) => {
      // Height report only, for the "chrome" fill's exact sizing.
      setContentHeight(height);
      lifecycle?.onResize?.(height);
    },
    onContentReady: () => {
      setStudioPainted(true);
      lifecycle?.onContentReady?.();
    },
    onError: (code, message) => {
      // The Studio's own diagnostic, in DEV. `message` is never shown to a
      // merchant — the host owns that copy in three languages — but it is the
      // only description of the failure that crosses the frame, and dropping
      // it silently leaves the error screen unexplainable from this side.
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error("[studio-host] studio:error", code, message ?? "(no detail)");
      }
      // Never navigate here. The Studio is the only template surface now, so
      // routing a failed gallery back to the gallery is a loop — the whole
      // reason this message exists instead of a close.
      setStudioError({ code, message });
      lifecycle?.onError?.(code, message);
    },
    onOverlay: (open) => {
      setOverlayOpen(open);
      lifecycle?.onOverlay?.(open);
    },
    onDim: (on) => {
      setDimOn(on);
      lifecycle?.onDim?.(on);
    },
    onScrollTo: (top, height) => {
      // Center the Studio element in the host's viewport. The dashboard
      // scrolls an inner container (<main class="overflow-auto">), so scroll
      // THAT when present — window.scrollTo is a no-op there.
      const frame = iframeRef.current;
      if (frame) {
        const fr = frame.getBoundingClientRect();
        const scroller = findScrollParent(frame);
        if (scroller) {
          const frameTopInScroller =
            fr.top - scroller.getBoundingClientRect().top + scroller.scrollTop;
          scroller.scrollTo({
            top: Math.max(
              0,
              frameTopInScroller + top + height / 2 - scroller.clientHeight / 2,
            ),
            behavior: "smooth",
          });
        } else {
          window.scrollTo({
            top: Math.max(
              0,
              fr.top + window.scrollY + top + height / 2 - window.innerHeight / 2,
            ),
            behavior: "smooth",
          });
        }
      }
      lifecycle?.onScrollTo?.(top, height);
    },
  });

  // Lock the dashboard's scroll behind the expanded frame, exactly like a
  // native modal would.
  useEffect(() => {
    if (!overlayOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [overlayOpen]);
  const { theme } = useAppTheme();
  const { t, i18n } = useTranslation();
  const studioTheme: "light" | "dark" = theme === "dark" ? "dark" : "light";
  const locale = i18n.language;

  // Boot theme, frozen at first mount. It rides the URL so the Studio can
  // paint its FIRST frame in the right theme instead of booting to its
  // embedded default (light) and correcting once our message lands.
  //
  // Frozen deliberately: `src` is an iframe attribute, so letting the live
  // theme into it would reload the Studio on every toggle and throw away the
  // editor session and any unsaved edits. Live changes travel over
  // studio:context below, which costs no reload.
  const bootThemeRef = useRef(studioTheme);

  // Boot locale, frozen for exactly the same reason as the theme above.
  //
  // Without it the Studio resolves its own chrome's language from ITS origin's
  // localStorage and then the browser — so a Nepali merchant opening the
  // editor for the first time got English panels inside a Nepali dashboard.
  // The theme hint has always been here and the locale one never was; they are
  // the same mechanism and the same one-line fix (Studio team, 2026-08-25).
  const bootLngRef = useRef(toStudioLng(locale));

  // The Studio's document (and its JS bundle) takes a moment to arrive; until
  // the iframe fires `load`, the frame area is blank white — which reads as an
  // error, not as loading. Cover it with the dashboard's own page skeleton so
  // the wait looks like every other page load.
  const [booted, setBooted] = useState(false);

  const query = new URLSearchParams();
  if (storeId) query.set("storeId", storeId);
  if (templateId) query.set("templateId", templateId);
  if (businessTypeCode) query.set("businessTypeCode", businessTypeCode);
  if (categoryPreset) query.set("categoryPreset", categoryPreset);
  if (picker) query.set("mode", "select");
  query.set("theme", bootThemeRef.current);
  query.set("lng", bootLngRef.current);
  const src = `${env.STUDIO_ORIGIN}${studioPath}?${query.toString()}`;

  // A new address is a new request, so an error reported for the old one no
  // longer applies. Onboarding's preview can mount before the store's template
  // id has loaded: the Studio answers that id-less request with NOT_FOUND, then
  // the id arrives, the frame navigates and the store renders — under a "could
  // not be found" overlay that nothing ever cleared.
  const errorSrcRef = useRef(src);
  useEffect(() => {
    if (errorSrcRef.current === src) return;
    errorSrcRef.current = src;
    setStudioError(null);
  }, [src]);

  // A new address or a retry remount is a new Studio document with its own
  // handshake to answer.
  useEffect(() => {
    readyAnsweredRef.current = false;
  }, [src, reloadKey]);

  // Keep the Studio's theme and language in step with the dashboard for the
  // rest of the session. The Studio's listener is persistent — it applies
  // every studio:context it receives, not just the first — so re-posting is
  // the whole mechanism; no Studio change is needed for this to work.
  //
  // Gated on `booted` because there is no contentWindow to post into before
  // the frame loads, and the load handler posts the opening context itself.
  const postCurrentContext = useCallback(() => {
    postContext({
      storeId,
      templateId,
      theme: studioTheme,
      // The same normalised code the boot `?lng=` carries, so the Studio is
      // never told two different spellings of one language.
      locale: toStudioLng(locale),
      selectedTemplateVersionId,
    });
  }, [
    postContext,
    storeId,
    templateId,
    studioTheme,
    locale,
    selectedTemplateVersionId,
  ]);
  postCurrentContextRef.current = postCurrentContext;

  useEffect(() => {
    if (!booted) return;
    postCurrentContext();
  }, [booted, postCurrentContext]);

  // One loader, held until the Studio has content on screen — no handoff to
  // the Studio's own loading state, which is what read as two page loads.
  //
  // Still a crossfade rather than a cut: content-ready is posted from a React
  // effect, so it can land a frame or two before the browser composites what
  // it announced. The skeleton lingers, transparent, across that gap.
  const [skeletonGone, setSkeletonGone] = useState(false);
  useEffect(() => {
    if (!studioPainted) return;
    const id = window.setTimeout(() => setSkeletonGone(true), SKELETON_FADE_MS);
    return () => window.clearTimeout(id);
  }, [studioPainted]);
  const showSkeleton = !skeletonGone && !studioError;

  /**
   * Reload the Studio from scratch. Declared here, below every piece of state
   * it resets, so nothing is referenced before its declaration.
   *
   * Remounting the iframe (via `reloadKey`) is the only way to re-run the
   * Studio's boot: re-assigning the same `src` is a no-op, and the sandbox
   * gives the host no handle on the inner document. Any editor session is
   * discarded — acceptable, because this only runs from a failed surface.
   */
  const retryStudio = useCallback(() => {
    setStudioError(null);
    setStudioPainted(false);
    setSkeletonGone(false);
    setBooted(false);
    setContentHeight(null);
    setReloadKey((n) => n + 1);
  }, []);

  // Never let a silent Studio trap the loader. A build predating
  // studio:content-ready, or one that has genuinely failed, is revealed on
  // this timer rather than sitting behind the skeleton forever.
  useEffect(() => {
    if (!booted || studioPainted) return;
    const id = window.setTimeout(
      () => setStudioPainted(true),
      STUDIO_PAINT_FALLBACK_MS[fill],
    );
    return () => window.clearTimeout(id);
  }, [fill, booted, studioPainted]);

  // A frame that loaded and then never said a word is not a slow frame.
  //
  // The Studio posts studio:ready from its mount — a quarter of a second in
  // practice — so silence this long after `load` means its messages are not
  // reaching us at all, and by far the likeliest reason is an origin mismatch
  // between the two builds. Naming that beats the alternative, which is the
  // paint fallback quietly revealing a frame that will never work.
  //
  // Deliberately generous: the cost of being wrong is showing an error over a
  // working screen, so this waits well past any plausible boot.
  useEffect(() => {
    if (!booted || heardFromStudio || studioError) return;
    const id = window.setTimeout(
      () => setStudioError({ code: "HOST_NO_RESPONSE" }),
      STUDIO_SILENCE_MS,
    );
    return () => window.clearTimeout(id);
  }, [booted, heardFromStudio, studioError]);

  // No Studio origin configured — say so, rather than failing three ways.
  //
  // This is a build-time env var (VITE_STUDIO_ORIGIN), and when it is missing
  // every downstream symptom is misleading: the iframe src collapses to a
  // RELATIVE url and the dashboard loads itself into its own frame, the auth
  // bridge bails out before installing its listener so the token handshake is
  // never answered, and the merchant eventually reads the Studio's "Timed out
  // waiting for host to respond with an editor token" — which points at the
  // host being slow when the truth is that it was never wired up.
  //
  // The deploy comment used to promise a fail-closed fallback to the in-app
  // editor. That editor is gone, so this is the fallback now. No retry: a
  // reload cannot fix a value baked in at build time.
  if (!env.STUDIO_ORIGIN) {
    return (
      <div
        role="alert"
        data-cy="studio-frame-unconfigured"
        className="flex flex-col items-center justify-center gap-3 rounded-lg px-4 py-10 text-center white-dark-bg-1"
        style={{
          minHeight: fill === "chrome" ? CHROME_MIN_HEIGHT : "100dvh",
          border: "1px solid var(--color-border)",
        }}
      >
        <AlertTriangle className="h-7 w-7 text-tertiary" aria-hidden="true" />
        <p className="m-0 max-w-sm text-sm font-medium text-primary">
          {t("templates.studio.error.NOT_CONFIGURED")}
        </p>
      </div>
    );
  }

  return (
    // The wrapper carries the height model so the skeleton can overlay the
    // frame area exactly.
    // "chrome": height tracks the Studio's reported document height
    // (studio:resize), so the frame never scrolls internally and the
    // dashboard page scrolls natively. Before the first report (or against a
    // Studio build that doesn't send studio:resize) the viewport-based
    // minimum keeps the frame usable — the Studio scrolls internally until
    // the report lands.
    // "viewport": the frame is the whole page; dvh tracks mobile toolbars.
    <div
      style={
        fill === "chrome"
          ? {
              position: "relative",
              width: "100%",
              // An explicit viewport wins over the reported document height:
              // the caller is declaring "this frame IS a window of this size".
              // A reported height is discarded the moment the Studio fails.
              // CHROME_MIN_HEIGHT below is only a FLOOR, and `height` beats
              // `min-height`, so a tall page reported just before the failure
              // kept the wrapper tall — and the error overlay, which centres
              // itself in this box with `inset-0`, centred in a box most of
              // which is below the fold. The merchant got a blank panel and
              // had to scroll to find out why. Dropping to `undefined` here
              // hands the height back to the floor, which is viewport-sized.
              height:
                viewportHeight != null
                  ? `${viewportHeight}px`
                  : studioError == null && contentHeight != null
                    ? `${contentHeight}px`
                    : undefined,
              // The floor stays on AFTER the first report, not just before it.
              //
              // A reported height is only as sensible as the page that
              // produced it, and the Studio's own failure screens are short —
              // an auth timeout renders a couple of lines. Tracking that
              // faithfully collapsed the frame to a stub directly under the
              // page header, and squeezed this component's own error overlay
              // (absolute inset-0) into the same stub, so the recovery UI was
              // the thing least readable at the moment it mattered.
              //
              // Costs nothing when the content is taller: `height` wins over
              // `min-height`. A legitimately short page just keeps its floor
              // instead of looking broken.
              //
              // Not applied when the caller passes an explicit viewport —
              // there the exact height IS the contract (onboarding paints a
              // 390x757 frame into a 300x582 phone screen, and a viewport
              // floor would burst the bezel).
              minHeight: viewportHeight != null ? undefined : CHROME_MIN_HEIGHT,
              // The canvas behind the frame, and it must be the SAME token the
              // page around it uses.
              //
              // The Studio deliberately paints `body { background: transparent }`
              // when embedded so the host's surface shows through — that is
              // what stops the two themes drifting apart across an iframe
              // boundary a parent cannot paint into. Which makes this
              // declaration the thing the merchant actually sees, and it was
              // the wrong token: the dashboard's content area is
              // `.white-dark-bg-1` (`dark:bg-surface-elevated`, #1F1F1F) while
              // this painted `--color-background` (#0A0A0A), so in dark mode
              // the frame sat on the page as a visibly darker rectangle.
              background: "var(--color-surface-elevated)",
            }
          : {
              position: "relative",
              width: "100%",
              height: "100dvh",
              // Same token for the chrome-less routes: the app root is also
              // `.white-dark-bg-1`.
              background: "var(--color-surface-elevated)",
            }
      }
    >
      <iframe
        // Remount on retry — see retryStudio. Nothing else changes this key,
        // so an ordinary re-render never reloads the Studio or drops an
        // in-progress editor session.
        key={reloadKey}
        ref={iframeRef}
        src={src}
        title={title}
        data-cy={dataCy}
        onLoad={() => {
          setBooted(true);
          postCurrentContext();
        }}
        // Fires when the browser cannot load the document at all — a dead
        // origin, DNS failure, or a CSP block. The Studio's JS never runs in
        // that case, so it can never tell us itself; without this the loader
        // just times out onto a blank frame.
        onError={() => setStudioError({ code: "LOAD_FAILED" })}
        // Deliberately narrow. allow-same-origin lets the Studio run its own
        // fetch/postMessage normally; it does NOT grant access to this origin.
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        allow="clipboard-write"
        // display: block kills the inline-image baseline gap under the frame.
        // Overlay mode is a pure style change on the SAME element — the
        // iframe must never remount (that would reload the Studio and drop
        // its session). z-index clears the dashboard's sticky chrome (header
        // z-20, sidebars z-30/z-50). The in-flow wrapper keeps its height, so
        // the page doesn't jump when the frame leaves the flow.
        style={
          overlayOpen
            ? {
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100dvh",
                zIndex: 60,
                border: "none",
                display: "block",
              }
            : {
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }
        }
      />
      {studioError && (
        /* Deliberately compact and self-contained rather than the full-page
           ErrorStatePage: this same frame renders inside onboarding's phone
           shell, whose screen is 300px wide. A page-scale error layout would
           overflow it. No navigation offered — that is what turns a failing
           gallery into a loop. */
        <div
          role="alert"
          data-cy="studio-frame-error"
          // Marks the host's own UI over the frame, so a host that swallows
          // clicks on the storefront (onboarding's preview) still lets
          // "Try again" through.
          data-studio-host-ui=""
          className="absolute inset-0 z-10 flex items-center justify-center overflow-auto p-4 white-dark-bg-1"
        >
          {/* Capped and centred rather than stretched: at 300px this column
              is the whole panel, while on a desktop route it stays readable
              instead of three words marooned across a wide empty field. */}
          <div className="flex w-full max-w-xs flex-col items-center gap-3 text-center">
            {/* A semantic tint behind the glyph, the way the transaction
                hero card and the dashboard empty states do it, rather than
                a bare grey triangle belonging to no design language. Both
                values are tokens, so the badge flips with the theme. */}
            <span
              className="flex h-11 w-11 flex-none items-center justify-center rounded-full"
              style={{ background: "var(--color-warning-bg)" }}
            >
              <AlertTriangle
                className="h-5 w-5"
                style={{ color: "var(--color-warning)" }}
                aria-hidden="true"
              />
            </span>
            {/* One line, not a title plus a description. The message is
                already code-specific, and every generic second line we
                could add under it is wrong for some code: retrying does
                not help SESSION_EXPIRED, and refreshing does not help
                NOT_CONFIGURED. */}
            <p className="m-0 text-base font-semibold text-primary">
              {t(`templates.studio.error.${studioError.code}`, {
                defaultValue: t("templates.studio.error.RENDER_ERROR"),
              })}
            </p>
            <CustomButton
              type="primary"
              size="small"
              icon={<RefreshCw className="h-3.5 w-3.5" />}
              onClick={retryStudio}
              data-cy="studio-frame-retry-btn"
            >
              {t("actions.tryAgain", { defaultValue: "Try again" })}
            </CustomButton>
          </div>
        </div>
      )}
      {showSkeleton && (
        <div
          role="status"
          aria-label={t("templates.studio.frameLoading")}
          // No padding of its own in "chrome" mode. The route content column
          // (DashboardLayout's `container … px-4 sm:px-6 lg:px-8`) already
          // insets this, so a second `p-4 sm:p-6` shifted this skeleton right
          // and down relative to the route-level one that renders immediately
          // before it — two loaders that should have looked like one, visibly
          // jumping between them.
          className={
            showSpinner
              ? // `white-dark-bg-1`, not `bg-gray-50`: that Tailwind colour has
                // no dark variant, so the spinner's backdrop stayed a pale
                // grey and flashed bright over a dark dashboard for the whole
                // load. Same token as the frame's own canvas above, so the
                // loader and what it covers are indistinguishable.
                "absolute inset-0 overflow-hidden flex items-center justify-center white-dark-bg-1"
              : "absolute inset-0 overflow-hidden white-dark-bg-1"
          }
          style={{
            opacity: studioPainted ? 0 : 1,
            transition: `opacity ${SKELETON_FADE_MS}ms ease`,
            // Transparent but still mounted — never intercept clicks on the
            // Studio that is now visible underneath it.
            pointerEvents: studioPainted ? "none" : undefined,
          }}
        >
          {showSpinner ? (
            // Deliberately the SAME spinner+copy the Studio's editor renders
            // while it fetches (identical markup and i18n key). Two different
            // loaders in a row — a dashboard-shaped skeleton, then the
            // Studio's spinner — read as two page loads; identical ones read
            // as one continuous wait.
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-tertiary m-0">
                {t("templates.page.loadingEditor")}
              </p>
            </div>
          ) : (
            // Same principle as the viewport spinner above: a replica of the
            // Studio gallery's own LoadingState, so our skeleton giving way to
            // theirs is invisible instead of a change of page shape. `picker`
            // carries the difference — that gallery has no page heading and no
            // guide button, so a replica showing them promises a title the
            // real thing never paints.
            <TemplateGallerySkeleton picker={picker} />
          )}
        </div>
      )}
      {dimOn &&
        dimHole &&
        createPortal(
          // Four veil strips covering the viewport around the hole. The color
          // MUST equal the Studio guide's own mask (TourSpotlight box-shadow
          // rgba) so the two dims read as one continuous overlay. Portaled to
          // <body> to escape the route-transition transform's stacking
          // context; z-index 55 clears all dashboard chrome (header z-20,
          // sidebars z-30/z-50) and stays under the overlay-expanded frame
          // (z-60).
          <div data-cy="studio-guide-veil" aria-hidden="true">
            {(
              [
                { top: 0, left: 0, right: 0, height: Math.max(0, dimHole.top) },
                { top: Math.max(0, dimHole.bottom), left: 0, right: 0, bottom: 0 },
                {
                  top: Math.max(0, dimHole.top),
                  left: 0,
                  width: Math.max(0, dimHole.left),
                  height: Math.max(0, dimHole.bottom - dimHole.top),
                },
                {
                  top: Math.max(0, dimHole.top),
                  left: Math.max(0, dimHole.right),
                  right: 0,
                  height: Math.max(0, dimHole.bottom - dimHole.top),
                },
              ] as React.CSSProperties[]
            ).map((pos, i) => (
              <div
                key={i}
                style={{
                  position: "fixed",
                  zIndex: 55,
                  background: "rgba(0, 0, 0, 0.58)",
                  ...pos,
                }}
              />
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
